import { ZoneFormCache, ZoneFormComponentProps, getLatestZone } from '../types'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  selectCleaningRoutes,
  selectCleaningZones,
  selectDrawingZoneType,
  selectMissionInfo,
  selectMissionTempZone,
  selectRestrictZones,
  selectSavingZoneProgress,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import {
  DEFAULT_FRONT_DUST_SUPPRESSION,
  DEFAULT_SCRUBBER_BRUSH_SPEED,
  DEFAULT_TURBINE_SPEED,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
} from 'utils/constants'
import { ICleaningZone, IZone, RootState } from 'utils/interfaces'

import EditingControlsInstructions from './EditingControlsInstructions'
import LaunchPointSettings from './LaunchPointSettings/LaunchPointSettings'
import RecordedRouteSelect from './RecordedRouteSelect'
import { resolveZoneEquipmentSetup, resolveZoneSetup } from './utils'

import style from './ZoneDetails.module.css'

const createCachedInputHandler = <K extends keyof ZoneFormCache, T extends NonNullable<ZoneFormCache[K]>>(
  setter: (value: T) => void,
  cacheKey: K,
  setCacheValue?: (key: keyof ZoneFormCache, value: any) => void
) => {
  return (value: T) => {
    setter(value)
    setCacheValue?.(cacheKey, value)
  }
}

const SHOW_ZONE_DESCRIPTION_FIELD = false

const ZoneDetail = ({
  onSaveClicked,
  zone,
  getCacheValue,
  setCacheValue,
  getAllCacheValues,
}: ZoneFormComponentProps) => {
  const { t } = useTranslation()
  const isRouteMode = useSelector(
    (state: RootState) =>
      state.mission.mode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || state.mission.mode === MISSION_PAGE_MODES.DRAW_ROUTE
  )
  const canUndo = useSelector(
    (state: RootState) =>
      (state.mission.mode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE ||
        state.mission.mode === MISSION_PAGE_MODES.DRAW_ROUTE) &&
      state.mission.tempZoneRouteHistory.length > 0
  )
  const isDrawingMode = useSelector(
    (state: RootState) =>
      state.mission.mode === MISSION_PAGE_MODES.DRAW_ROUTE || state.mission.mode === MISSION_PAGE_MODES.DRAW_ZONE
  )
  const drawingZoneType = useSelector(selectDrawingZoneType)
  const cleaningZones = useSelector(selectCleaningZones)
  const restrictZones = useSelector(selectRestrictZones)
  const cleaningRoutes = useSelector(selectCleaningRoutes)

  // Generate auto-incremented default name based on existing items
  const getDefaultName = () => {
    if (zone?.properties?.name) return zone.properties.name
    if (isRouteMode) {
      return `Route ${cleaningRoutes.length + 1}`
    }
    if (drawingZoneType === MISSION_ZONE_TYPES.RESTRICT) {
      return `Restricted Zone ${restrictZones.length + 1}`
    }
    return `Zone ${cleaningZones.length + 1}`
  }

  const defaultName = getDefaultName()
  const missionInfo = useSelector(selectMissionInfo)
  const isSlamMode = missionInfo?.facility_id ? !!missionInfo?.location : undefined

  const getInitialValue = <K extends keyof ZoneFormCache>(key: K, defaultValue: ZoneFormCache[K]): ZoneFormCache[K] => {
    const cached = getCacheValue?.(key)
    return cached !== undefined && cached !== null ? cached : defaultValue
  }

  const [selectedZoneType, setSelectedZoneType] = useState<MISSION_ZONE_TYPES>(() => {
    return (
      getInitialValue(
        'selectedZoneType',
        zone?.properties?.zone_type || drawingZoneType || MISSION_ZONE_TYPES.CLEANING
      ) || MISSION_ZONE_TYPES.CLEANING
    )
  })
  const [name, setName] = useState(() => {
    return getInitialValue('name', zone?.properties?.name || defaultName) || defaultName
  })
  const [description, setDescription] = useState(() => {
    return getInitialValue('description', zone?.properties?.description || '') || ''
  })
  const [isBorderCleaning, setIsBorderCleaning] = useState(() => {
    return (
      getInitialValue('isBorderCleaning', (zone as ICleaningZone)?.properties?.is_border_cleaning || false) || false
    )
  })

  // Sync selectedZoneType and name with drawingZoneType when it changes (e.g. user switches between Cleaning/Restricted in the dropdown)
  useEffect(() => {
    if (!zone && drawingZoneType) {
      setSelectedZoneType(drawingZoneType)
      setName(getDefaultName())
      setCacheValue?.('selectedZoneType', drawingZoneType)
      setCacheValue?.('name', getDefaultName())
    }
  }, [drawingZoneType, zone])

  const tempZone = useSelector(selectMissionTempZone)
  const savingProgress = useSelector(selectSavingZoneProgress)

  const latestZone = getLatestZone(cleaningZones, zone?.properties?.zone_id)

  const zoneIdRef = useRef<number | undefined>(zone?.properties?.zone_id)

  const applyCacheOrZone = <K extends keyof ZoneFormCache>(
    key: K,
    zoneValue: ZoneFormCache[K] | undefined,
    defaultValue: ZoneFormCache[K]
  ): ZoneFormCache[K] => {
    const cached = getCacheValue?.(key)
    return cached !== undefined && cached !== null ? cached : zoneValue !== undefined ? zoneValue : defaultValue
  }

  useEffect(() => {
    const currentZoneId = zone?.properties?.zone_id
    if (zoneIdRef.current !== currentZoneId) {
      zoneIdRef.current = currentZoneId

      const zoneToUse = zone ? latestZone || zone : null
      const props = zoneToUse?.properties

      if (zoneToUse) {
        setName(applyCacheOrZone('name', props?.name, defaultName) || defaultName)
        setDescription(applyCacheOrZone('description', props?.description, '') || '')
        setSelectedZoneType(
          (applyCacheOrZone('selectedZoneType', props?.zone_type as MISSION_ZONE_TYPES, MISSION_ZONE_TYPES.CLEANING) ||
            MISSION_ZONE_TYPES.CLEANING) as MISSION_ZONE_TYPES
        )
        setIsBorderCleaning(
          (applyCacheOrZone('isBorderCleaning', (zoneToUse as ICleaningZone)?.properties?.is_border_cleaning, false) ||
            false) as boolean
        )

        if (selectedZoneType !== MISSION_ZONE_TYPES.RESTRICT) {
          // No need to reset speed states here anymore, they moved to Cleaning tab
        }
      } else {
        setName(defaultName)
        setDescription('')
        setSelectedZoneType(MISSION_ZONE_TYPES.CLEANING)
        setIsBorderCleaning(false)
      }
    }
  }, [zone?.properties?.zone_id, latestZone, selectedZoneType, missionInfo, defaultName, getCacheValue])

  const [tempRouteName, setTempRouteName] = useState(() => {
    return getInitialValue('tempRouteName', '') || ''
  })

  const [routeSegments, setRouteSegments] = useState<Array<{ id: number; name: string }>>([])

  // Initialize segments logic...
  useEffect(() => {
    if (tempRouteName && routeSegments.length === 0) {
      // We could potentially parse tempRouteName if it has "+",
      // but for now let's just make one segment if we have a route name but empty list.
      // This handles the "single route loaded" case roughly.
      // However, we don't have the ID here easily without more complex logic or useSelector.
    }
  }, [])

  const [showModal, setShowModal] = useState(false)
  const changeZoneType = createCachedInputHandler(setSelectedZoneType, 'selectedZoneType', setCacheValue)
  const inputFile = useRef<HTMLInputElement | null>(null)

  const onSave = (keepEditing?: boolean) => {
    const zoneToUse = latestZone || zone
    const cache = getAllCacheValues?.()
    const equipment_setup = resolveZoneEquipmentSetup(cache, zoneToUse)
    const setup = resolveZoneSetup(cache, zoneToUse)

    return onSaveClicked({
      zoneName: name,
      zoneDescription: description,
      zoneType: selectedZoneType,
      cleaningSpeed:
        cache?.cleaningSpeed ??
        (zoneToUse as ICleaningZone)?.properties?.cleaning_speed ??
        missionInfo?.cleaning_speed ??
        0.5,
      transportSpeed:
        cache?.transportSpeed ??
        (zoneToUse as ICleaningZone)?.properties?.transport_speed ??
        missionInfo?.transport_speed ??
        0.5,
      isBorderCleaning:
        cache?.isBorderCleaning ?? (zoneToUse as ICleaningZone)?.properties?.is_border_cleaning ?? false,
      equipment_setup,
      setup,
      keepEditing,
    })
  }

  const onRecalculate = async () => {
    try {
      await onSave(true)
      const zoneToUse = latestZone || zone
      if (zoneToUse?.properties?.zone_id) {
        await store.dispatch(missionThunks.startCalculatingRoute(zoneToUse.properties.zone_id))
      }
    } catch (error) {
      console.error('Failed to save or recalculate route:', error)
    }
  }

  const onCancelClicked = () => {
    store.dispatch(missionThunks.cancelEditingZone())
  }

  const savingProgressInWork = useMemo(() => {
    return savingProgress === PROGRESS_TYPES.WORK
  }, [savingProgress])

  const handleSelectRecordedRoute = async (name: string, id?: number, indexToReplace?: number) => {
    if (!id) {
      console.error('No ID provided')
      setShowModal(false)
      return
    }

    changeZoneType(MISSION_ZONE_TYPES.RECORDED)

    let newSegments = [...routeSegments]

    if (indexToReplace !== undefined && indexToReplace !== null) {
      if (indexToReplace < newSegments.length) {
        newSegments[indexToReplace] = { id, name }
      } else {
        newSegments.push({ id, name })
      }
    } else {
      newSegments.push({ id, name })
    }

    setRouteSegments(newSegments)
    const compositeName = newSegments.map(s => s.name).join(' + ')
    createCachedInputHandler(setTempRouteName, 'tempRouteName', setCacheValue)(compositeName)

    store.dispatch(
      missionThunks.setCompositeRoute(
        newSegments.map(s => s.id),
        zone
      )
    )

    setShowModal(false)
    // activeSegmentIndex removal handled by deleting state definition
  }

  const onRemoveSegmentClick = (index: number) => {
    const newSegments = [...routeSegments]
    newSegments.splice(index, 1)
    setRouteSegments(newSegments)

    const compositeName = newSegments.map(s => s.name).join(' + ')
    createCachedInputHandler(setTempRouteName, 'tempRouteName', setCacheValue)(compositeName)

    if (newSegments.length > 0) {
      store.dispatch(
        missionThunks.setCompositeRoute(
          newSegments.map(s => s.id),
          zone
        )
      )
    } else {
      store.dispatch(missionThunks.clearTempRoute())
    }
  }

  return (
    <div className={style.root}>
      <div className={style.content} style={{ flex: '0 1 auto' }}>
        <TextFieldDescribed
          className={style.textfield}
          description={!isRouteMode ? t('zone_details.zone_name') : t('zone_details.route_name')}
          defaultValue={name}
          placeholder={
            !isRouteMode ? t('zone_details.zone_name_placeholder') : t('zone_details.route_name_placeholder')
          }
          onChange={e => createCachedInputHandler(setName, 'name', setCacheValue)(e.target.value)}
        />
        {SHOW_ZONE_DESCRIPTION_FIELD && (
          <TextFieldDescribed
            className={style.textfield}
            description={t('zone_details.zone_description')}
            defaultValue={description}
            placeholder={t('zone_details.zone_description_placeholder')}
            onChange={e => createCachedInputHandler(setDescription, 'description', setCacheValue)(e.target.value)}
          />
        )}
        {isRouteMode && selectedZoneType === MISSION_ZONE_TYPES.RECORDED && (
          <div className={style.selectFileBlock}>
            <div className={clsx('text-14-400', style.subtitle)}>{t('zone_details.select_recorded_route')}</div>
            {/* Render all selected segments */}
            {routeSegments.map((segment, index) => (
              <div key={index} className={style.segmentRow} style={{ alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <RecordedRouteSelect
                    value={segment.name}
                    onSelect={(name, id) => {
                      handleSelectRecordedRoute(name, id, index)
                    }}
                    placeholder={t('zone_details.select_recorded_route')}
                    isSlamMode={isSlamMode}
                  />
                </div>
                <div
                  className={style.buttonSmall}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '40px',
                    cursor: 'pointer',
                  }}
                  onClick={() => onRemoveSegmentClick(index)}
                >
                  <RemoveSvg className={style.removeIcon} />
                </div>
              </div>
            ))}

            <div className={style.segmentRow} style={{ alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <RecordedRouteSelect
                  onSelect={handleSelectRecordedRoute}
                  placeholder={t('zone_details.recorded_route_not_selected')}
                  isSlamMode={isSlamMode}
                />
              </div>
              <div
                className={style.buttonSmall}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}
              >
                <RemoveSvg className={style.removeIcon} style={{ opacity: 0.3 }} />
              </div>
            </div>
          </div>
        )}
        {selectedZoneType !== MISSION_ZONE_TYPES.RESTRICT && (
          <div className={style.content}>{/* Border cleaning moved to Equipment Settings */}</div>
        )}
      </div>
      {selectedZoneType !== MISSION_ZONE_TYPES.RESTRICT && !isRouteMode && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexShrink: 0,
          }}
        >
          <div className={style.textAlignLeft}>{t('zone_details.start_point')}</div>
          <div className={style.alignRight}>
            <LaunchPointSettings />
          </div>
        </div>
      )}
      <EditingControlsInstructions />
      <div className={style.buttons} style={{ justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex' }}>
          <Button.Outlined className={style.button} onClick={onCancelClicked} disabled={savingProgressInWork}>
            {t('common.cancel')}
          </Button.Outlined>
          <Button.Standart
            className={style.button}
            onClick={() => onSave()}
            disabled={(!tempZone && tempRouteName === '') || savingProgressInWork}
            loading={savingProgressInWork}
          >
            {t('common.save')}
          </Button.Standart>
        </div>
      </div>
    </div>
  )
}

export default ZoneDetail
